<script lang="ts" setup>
import { useModal } from 'vue-final-modal'

import BasicFloatingButton from '~/components/form-elements/BasicFloatingButton.vue'
import BackInStockNotification from '~/components/modal-windows/BackInStockNotification.vue'
import AiReview from '~/components/products/AiReview.vue'
import ProductImagesGallery from '~/components/products/ProductImagesGallery.vue'
import ProductLabels from '~/components/products/ProductLabels.vue'
import ProductSpecialButton from '~/components/products/ProductSpecialButton.vue'
import ProductText from '~/components/products/ProductText.vue'
import ProductTitle from '~/components/products/ProductTitle.vue'
import ProductVariations from '~/components/products/ProductVariations.vue'
import RatingComponent from '~/components/products/Rating.vue'
import { useAnchor } from '~/composables/anchor'
import { useResponsiveScreenFacade } from '~/composables/responsive-screen-facade'
import constants from '~/constants'
import { ProductAttribute } from '~/enums/product-attribute'
import { ProductLabelUtils } from '~/enums/product-label'
import helpers from '~/helpers'
import type { ProductData } from '~/types/products'

const props = defineProps<{
  aiSummaryText: string | null
  productData: ProductData
}>()

let unitQuantity = null
let servingsPerPackage = null

const { isMd } = useResponsiveScreenFacade()

const canBeOrdered = computed(() => !isOutOfStock.value || isOnPreOrder.value)
const galleriesData = computed(() => props.productData.relationships.galleries.data)
const heading = computed(() => seoData.value?.h1 ?? name.value)
const imagesData = computed(() => props.productData.relationships.images.data)
const isHalal = computed(() => props.productData.meta.isHalal)
const isOnlyAvailableInMoscow = computed(() => props.productData.attributes.isOnlyAvailableInMoscow)
const isOnPreOrder = computed(() => props.productData.meta.isOnPreOrder)
const isOutOfStock = computed(() => props.productData.meta.isOutOfStock)
const name = computed(() => props.productData.attributes.name)
const priceWithPromoCode = computed(() => {
  const { priceWithPromoCode } = props.productData.attributes

  if (typeof priceWithPromoCode === 'number') {
    return Math.ceil(priceWithPromoCode)
  }

  return null
})
const rating = computed(() => {
  const { rating } = props.productData.attributes

  return typeof rating === 'number' ? rating : null
})
const seoData = computed(() => props.productData.attributes.seoData)
const tagsData = computed(
  () => (props.productData.relationships.tags?.data ?? [])
    .filter((x: any): boolean => !ProductLabelUtils.hasDescription(x.attributes.name))
)
const variations = computed(() => props.productData.meta.variations ?? [])
const videos = computed(() => props.productData.meta.videos ?? [])
const warning = computed(() => props.productData.attributes.warning)

async function openBackInStockNotificationModalWindow (): Promise<void> {
  await useModal({ component: BackInStockNotification, attrs: { productId: props.productData.id } }).open()
}

for (const { id, value } of props.productData.meta.attributes) {
  if (id === ProductAttribute.ServingsPerPackage) {
    servingsPerPackage = value
  } else if (id === ProductAttribute.UnitQuantity) {
    unitQuantity = value
  }
}

useAnchor()
</script>

<template>
  <DefaultBox class="flex flex-col md:flex-row">
    <div class="mb-5 md:mb-0 md:w-[calc(50%-12px)]">
      <ProductImagesGallery
        :class="{ 'grayscale': helpers.isProductImageBlackAndWhite(productData) }"
        :galleries-data="galleriesData"
        :images-data="imagesData"
        :product-name="name"
        :videos="videos"
      >
        <template #overlay>
          <div class="absolute right-3 top-3 z-10 md:right-4 md:top-4">
            <SvgIcon
              v-if="isHalal"
              class="!size-10 text-main-green"
              name="labels/halal"
            />
          </div>
        </template>
      </ProductImagesGallery>
    </div>

    <div class="hidden md:block md:w-6" />

    <div class="md:w-[calc(50%-12px)]">
      <ProductTitle class="mb-3 md:mb-4" :title="heading" />

      <ProductLabels class="mb-4 md:mb-6" is-product-page :product-data="productData" />

      <TaxonomyCarousel v-if="tagsData.length" class="-ml-2 md:mb-7 md:ml-0" :tags-data="tagsData" />

      <Teleport defer :disabled="isMd" to="#aside-body">
        <ProductText
          v-if="(unitQuantity || servingsPerPackage)"
          class="!mb-2 md:!mb-3"
          :text="unitQuantity? 'Количество штук в\xA0упаковке' : 'Количество порций в\xA0упаковке'"
        />

        <div
          v-if="(unitQuantity || servingsPerPackage) && priceWithPromoCode"
          class="mb-7 flex flex-wrap"
        >
          <div class="relative">
            <ProductSpecialButton
              v-if="servingsPerPackage"
              :is-active="Boolean(canBeOrdered)"
              :is-disabled="!canBeOrdered"
              :text="`${Math.ceil(priceWithPromoCode / Number(servingsPerPackage))}\xA0₽\xA0/\xA0порц.`"
              :title="`${servingsPerPackage}\xA0порц.`"
            />
            <ProductSpecialButton
              v-else-if="unitQuantity"
              :is-active="Boolean(canBeOrdered)"
              :is-disabled="!canBeOrdered"
              :text="`${Math.ceil(priceWithPromoCode / Number(unitQuantity))}\xA0₽\xA0/\xA0шт.`"
              :title="`${unitQuantity}\xA0шт.`"
            />

            <BasicFloatingButton
              v-if="!canBeOrdered"
              icon="outlined/bell"
              :title="'Уведомить о\xA0поступлении'"
              @click.prevent="openBackInStockNotificationModalWindow"
            />
          </div>
        </div>

        <ProductVariations :variations="variations" />
      </Teleport>

      <Teleport defer :disabled="isMd" to="#aside-footer">
        <a class="mb-2 flex items-center !no-underline" href="#anchor4">
          <RatingComponent v-if="rating !== null" class="mr-2" is-product-page :rating="rating" />

          <span class="mr-2 block text-sm leading-[1.43] text-main-silver md:leading-[1.71]">
            {{ productData.attributes.totalPublishedReviews === 0 ? 'Нет отзывов' : `${productData.attributes.totalPublishedReviews}\xA0${helpers.getDeclension(productData.attributes.totalPublishedReviews, constants.wordForms.review)}` }}
          </span>

          <SvgIcon class="!size-4" name="outlined/ui-next-arrow" />
        </a>

        <AiReview :ai-summary-text="aiSummaryText" class="mb-4" />
      </Teleport>

      <Notification v-if="warning" class="mb-4 md:mb-5" status="warning">
        {{ warning }}
      </Notification>

      <Notification v-if="isOnlyAvailableInMoscow" class="mb-4 md:mb-5" is-small status="warning">
        Доставляется только по&nbsp;Москве.
      </Notification>
    </div>
  </DefaultBox>
</template>
